import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import _get from 'lodash/get';
import _merge from 'lodash/merge';

import {
  email as emailValidator,
  url as urlValidator,
  storeName as storeNameValidator,
} from '@klarna/mf-validations';
import { selectors as i18nSelectors, translator } from 'mage-i18n';
import { BorderlessDropdown, SpacerHorizontal, SpacerVertical } from '@klarna/bubble-ui';
import {
  HorizontalWrapper,
  Input,
  formUtils,
  FieldsetForObject,
  defaultContainerPropTypes,
  payloadUtils,
  prependHttpScheme,
} from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { validators } from '@/flow/validation';
import { withCapabilityBeforeNext } from '@/flow/common';
import { logEvent } from '@/utils/analytics';
import { CAPS, APP_PATHS, FLOW_STEP_PATHS } from '@/constants';
import { BackButton, CallToActionButton, ContainerTemplate } from '@/components';
import { getGoodsCategories } from '@/selectors/options';
import { getMerchantAwareTranslator, isNewSplitLayout } from '@/selectors/merchant';
import { isUnmanagedKyc } from '@/selectors/widget';

import { getDefaultState } from '../selectors';
import { CY_STORE_DETAILS_CONTAINER_IDS } from '../constants';
import { ANALYTICS_EVENTS } from '../analyticsEvents';

const tPrefix = 'products-fe.containers.store_details';

export class DefaultStoreDetailsContainer extends React.Component {
  constructor(props) {
    super(props);

    this.getBody = this.getBody.bind(this);
    this.onChangeFields = this.onChangeFields.bind(this);
    this.onNext = this.onNext.bind(this);
    this.prepareFields = this.prepareFields.bind(this);

    const { fields } = this.prepareFields();

    this.state = {
      fields,
      language: props.language,
      initialState: fields,
    };
  }

  componentDidMount() {
    const { history, basePath } = this.props;

    window.onpopstate = e => {
      // we check for pathname to see if we are navigating towards store-details
      // if we are, then we don't want to redirect to product selection.
      const pathname = _get(e, 'target.location.pathname', null);
      if (pathname && !pathname.includes(FLOW_STEP_PATHS.STORE_DETAILS_PATH)) {
        // this redirect here is done so that when the user uses the browser native "back" button
        // so we can keep them on store-details page
        history.push(`${basePath}/${APP_PATHS.PRODUCT_SELECTION_PATH}`);
      }
    };
  }

  prepareFields() {
    const {
      defaultState = {},
      isNewSplitLayout,
      isUnmanagedKyc,
      goodsCategories,
      t,
      tMerchantAware,
    } = this.props;
    const { fields } = this.state || {};
    const values = formUtils.getMappedValues(fields);
    const { storeName = '', website = '', email = '', goodsCategory = '' } = _merge(
      defaultState,
      values,
    );

    const shouldRenderWebsite =
      (isNewSplitLayout && !website) || !isNewSplitLayout || storeName || !isUnmanagedKyc;

    return formUtils.initFormFromState({
      fields: {
        storeName: {
          component: Input,
          size: 1,
          type: 'text',
          label: tMerchantAware(`${tPrefix}.fields.store_name.label`),
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: tMerchantAware(`${tPrefix}.fields.store_name.errors.required`),
            },
            {
              callback: validators.common.isString({ min: 2 }),
              errorMessage: tMerchantAware(`${tPrefix}.fields.store_name.errors.invalid`),
            },
            {
              callback: storeNameValidator.validate,
              errorMessage: tMerchantAware(
                `${tPrefix}.fields.store_name.errors.invalid_characters`,
              ),
            },
          ],
          isInitialized: false,
          isValid: false,
          value: storeName,
          extraProps: {
            autoFocus: true,
            ...generateCyElemId(CY_STORE_DETAILS_CONTAINER_IDS.INPUT_STORE_NAME, window),
          },
        },
        ...(shouldRenderWebsite && {
          website: {
            component: Input,
            size: 1,
            type: 'text',
            label: t(`${tPrefix}.fields.website.label`),
            validators: [
              {
                callback: validators.common.isRequired,
                errorMessage: t(`${tPrefix}.fields.website.errors.required`),
              },
              {
                callback: urlValidator.validate,
                errorMessage: t(`${tPrefix}.fields.website.errors.invalid`),
              },
            ],
            isInitialized: false,
            isValid: false,
            value: website,
            extraProps: {
              ...generateCyElemId(CY_STORE_DETAILS_CONTAINER_IDS.INPUT_WEBSITE, window),
            },
          },
        }),
        email: {
          component: Input,
          size: 1,
          type: 'text',
          label: t(`${tPrefix}.fields.email.label`),
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: t(`${tPrefix}.fields.email.errors.required`),
            },
            {
              callback: emailValidator.validate,
              errorMessage: t(`${tPrefix}.fields.email.errors.invalid`),
            },
          ],
          isInitialized: false,
          isValid: false,
          value: email,
          extraProps: {
            ...generateCyElemId(CY_STORE_DETAILS_CONTAINER_IDS.INPUT_EMAIL, window),
          },
        },
        goodsCategory: {
          component: BorderlessDropdown,
          size: 1,
          label: t(`${tPrefix}.fields.goods_category.label`),
          validators: [
            {
              callback: validators.common.isRequired,
              errorMessage: t(`${tPrefix}.fields.goods_category.errors.required`),
            },
          ],
          extraProps: {
            options: goodsCategories,
            ...generateCyElemId(CY_STORE_DETAILS_CONTAINER_IDS.SELECT_CATEGORY, window),
          },
          isInitialized: false,
          isValid: false,
          value: goodsCategory,
        },
      },
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.language !== this.props.language) {
      const { fields } = this.prepareFields();
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({ fields, language: this.props.language });
    }
  }

  componentWillUnmount() {
    window.onpopstate = () => {};
  }

  onChangeFields(fields) {
    this.setState({ fields });
  }

  getBody({ fields }) {
    const { defaultState = {}, isNewSplitLayout, isUnmanagedKyc } = this.props;
    const { storeId, storeName, website = '' } = defaultState;

    const shouldRenderWebsite =
      (isNewSplitLayout && !website) || !isNewSplitLayout || storeName || !isUnmanagedKyc;

    const body = formUtils.getMappedValues(fields);
    body.storeId = storeId;
    body.website = shouldRenderWebsite ? prependHttpScheme(body.website) : website;
    return body;
  }

  onNext() {
    this.setState({
      ...this.state,
      fields: formUtils.initializeFields(this.state.fields),
    });

    formUtils.callToActionOnNextWrapper(this.state.fields, () => {
      this.props.onNext(
        payloadUtils.getMerchantCapabilityPayload(this.props.mid, this.state, this.getBody),
      );
    });
  }

  render() {
    const { error, hasBack, isLoading, isDisabled = false, onBack, t, tMerchantAware } = this.props;
    const { fields } = this.state;
    const tabIndex = Object.keys(fields).length + 1;
    return (
      <ContainerTemplate
        title={tMerchantAware(`${tPrefix}.title`)}
        subtitle={tMerchantAware(`${tPrefix}.subtitle`)}
        error={error}
      >
        <SpacerVertical small />
        <FieldsetForObject
          name="store-details"
          isBorderless
          isDisabled={this.props.isDisabled}
          fields={fields}
          onBlur={key => formUtils.onBlurFields(key, this.state, this.onChangeFields)}
          onChange={(key, val) =>
            formUtils.onChangeFields(key, val, this.state, this.onChangeFields)
          }
        />
        <SpacerVertical large />
        <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-end">
          {hasBack && (
            <>
              <BackButton
                tabIndex={tabIndex + 1}
                onClick={onBack}
                disabled={isDisabled}
                {...generateCyElemId(CY_STORE_DETAILS_CONTAINER_IDS.BTN_CHANGE_PRODUCTS, window)}
              >
                {t(`${tPrefix}.buttons.back`)}
              </BackButton>
              <SpacerHorizontal medium />
            </>
          )}
          <CallToActionButton
            tabIndex={tabIndex}
            onClick={this.onNext}
            loading={isLoading}
            disabled={isDisabled}
            {...generateCyElemId(CY_STORE_DETAILS_CONTAINER_IDS.BTN_SUBMIT, window)}
          >
            {t(`${tPrefix}.buttons.next`)}
          </CallToActionButton>
        </HorizontalWrapper>
      </ContainerTemplate>
    );
  }
}

DefaultStoreDetailsContainer.propTypes = {
  ...defaultContainerPropTypes,
  language: PropTypes.string.isRequired,
  mid: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  tMerchantAware: PropTypes.func.isRequired,
  goodsCategories: PropTypes.array,
};

const WrappedComponent = compose(
  connect(state => ({
    defaultState: getDefaultState(state),
    goodsCategories: getGoodsCategories(state),
    isNewSplitLayout: isNewSplitLayout(state),
    isUnmanagedKyc: isUnmanagedKyc(state),
    language: i18nSelectors.language(state),
    t: translator(state),
    tMerchantAware: getMerchantAwareTranslator(state),
  })),
  withCapabilityBeforeNext({ cap: CAPS.V2_MERCHANTS_STORE_DETAILS_PUT }, ({ payload }) => {
    const fields = payload.body;
    logEvent(ANALYTICS_EVENTS.fillStoreDetails, fields);
  }),
)(DefaultStoreDetailsContainer);

WrappedComponent.onboardingTypes = '*';
WrappedComponent.deploymentRegionsAndCountries = {
  us: ['fallback'],
  eu: ['fallback'],
  ap: ['fallback'],
};

WrappedComponent.fieldsToFill = [
  {
    fieldName: 'businessDetails',
    fields: {
      email: {},
      goodsCategory: {},
      storeName: {},
      website: {},
    },
  },
];

export default WrappedComponent;
